/**
 * @ignore
 * Created by daletan on 8/25/16.
 */

'use strict';

import get from 'lodash.get';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay/classic';

import { FormattedMessage, FormattedNumber } from 'react-intl';

// Stores

import ItemStore from '../../stores/ItemStore.es';
import UserStore from '../../../global/stores/UserStore.es';

import { OPEN_DETAILS_FROM_SIDEBAR } from '../../constants/shippingConstants';

// Components

import QuotePrimary from './QuotePrimary';
import ShippingDetailsLink from './ShippingDetailsLink';
import ShippingIcon from 'dibs-vg/dist/react/shipping';

// Helpers

import provideContext from 'fluxible-addons-react/provideContext';
import connectToStores from 'fluxible-addons-react/connectToStores';
import { filterQuotes } from './shippingHelpers';
import copy from './Blurb.json';
import styles from '../../../../scss/new-pdp/pdp-shipping-blurb.scss';

const ShippingBlurb = props => {
    const canShowBlurb = !(
        props.isSold ||
        props.isUnlisted ||
        props.hidePrice ||
        !props.currency ||
        !props.relayItem
    );
    const {
        user = null,
        conversation,
        quotes,
        currency,
        context,
        hasUserId,
        userId,
        itemId,
        dealerPk,
        viewer,
        dealerData,
        isPrint,
    } = props;

    const pdpBundleEnabled = get(viewer, 'pdpBundleEnabled');

    if (canShowBlurb) {
        const customQuotes =
            pdpBundleEnabled &&
            (get(conversation, 'transaction.shipmentQuotes') || []).filter(
                quote => quote.isCustom && quote.status !== 'NEW'
            );
        const customQuote = customQuotes.length && customQuotes[customQuotes.length - 1];
        const hasQuote =
            filterQuotes(quotes.domestic).length ||
            filterQuotes(quotes.local).length ||
            customQuote;
        const isVerifiedTrade = get(user, 'canSeeTradeReturns', false);
        const quoteRequestForAllIsEnabled = get(viewer, 'quoteRequestForAllIsEnabled');
        const canRequest = hasUserId && (quoteRequestForAllIsEnabled || isVerifiedTrade);
        const labelText = hasQuote ? copy.hasQuote : copy.noQuote;
        const link = (
            <a href={labelText.href}>
                {canRequest ? labelText.canRequest : labelText.cannotRequest}
            </a>
        );

        let customQuoteAmount;
        let customQuoteFormattedAmount;
        let customQuoteCopy;

        if (customQuote) {
            const customQuoteIsRequest = customQuote.status === 'REQUESTED';
            if (customQuoteIsRequest) {
                customQuoteCopy = (
                    <FormattedMessage
                        id="pdp.Blurb.customQuotePending"
                        defaultMessage="Custom quote pending"
                    />
                );
            } else {
                customQuoteAmount = (
                    get(customQuote, 'totalAmount.convertedAmountList') || []
                ).find(amount => amount.currency === currency);
                customQuoteFormattedAmount = customQuoteAmount ? (
                    <FormattedNumber
                        value={customQuoteAmount.amount}
                        style="currency"
                        currency={currency}
                    />
                ) : null;

                customQuoteCopy = (
                    <FormattedMessage
                        id="pdp.Blurb.customQuote"
                        defaultMessage="{amount} Custom Quote"
                        values={{ amount: customQuoteFormattedAmount }}
                    />
                );
            }
        }

        return (
            <div className="pdp-sidebar-icon pdp-shipping-blurb">
                <div className={`${styles.icon} shipping-blurb-icon`}>
                    <ShippingIcon />
                </div>
                <div className={`${styles.info} shipping-blurb-info`}>
                    <span className={styles.title}>Shipping: </span>
                    <div className={styles.quote}>
                        {!!customQuote && <div>{customQuoteCopy}</div>}
                        {!customQuote && <QuotePrimary currency={currency} quotes={quotes} />}
                    </div>
                    {!props.isOnHold &&
                        !isPrint && (
                            <div className={`${styles.link} shipping-blurb-link`}>
                                <ShippingDetailsLink
                                    hasUserId={hasUserId}
                                    userId={userId}
                                    itemId={itemId}
                                    dealerPk={dealerPk}
                                    viewer={viewer}
                                    link={link}
                                    context={context}
                                    user={user}
                                    quotes={quotes}
                                    parentName={'blurb'}
                                    customQuoteAvailable={hasUserId}
                                    regionType="DOMESTIC"
                                    source={OPEN_DETAILS_FROM_SIDEBAR}
                                    dealerData={dealerData}
                                    trackingLocation="pdp sidebar"
                                    item={props.relayItem}
                                />
                            </div>
                        )}
                </div>
            </div>
        );
    }
    return <div className="pdp-shipping-blurb no-data" />;
};

ShippingBlurb.propTypes = {
    context: PropTypes.object,
    conversation: PropTypes.object,
    quotes: PropTypes.object.isRequired,
    user: PropTypes.object,
    hasUserId: PropTypes.bool,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    isPrint: PropTypes.bool,
    dealerPk: PropTypes.string,
    viewer: PropTypes.object,
    relayItem: PropTypes.object,
    currency: PropTypes.string.isRequired,
    isOnHold: PropTypes.bool,
    isSold: PropTypes.bool,
    isUnlisted: PropTypes.bool,
    hidePrice: PropTypes.bool,
    dealerData: PropTypes.object,
    pagePosition: PropTypes.string,
};

export default Relay.createContainer(
    provideContext(
        connectToStores(ShippingBlurb, [ItemStore, UserStore], context => {
            const userStore = context.getStore(UserStore);
            const itemStore = context.getStore(ItemStore);
            return {
                currency: userStore.getCurrency(),
                isOnHold: itemStore.getIsOnHold(),
                isSold: itemStore.getIsSold(),
                isUnlisted: itemStore.getIsUnlisted(),
                hidePrice: itemStore.getHidePrice(),
            };
        })
    ),
    {
        initialVariables: {
            hasUserId: false,
            userId: null,
            itemId: null,
        },
        fragments: {
            user: ({ hasUserId, itemId, userId }) => Relay.QL`
            fragment on User {
                canSeeTradeReturns: hasPermission(name: "B_TRADE_RETURN_POLICY")
                ${ShippingDetailsLink.getFragment('user', { hasUserId, itemId, userId })}
            }
        `,
            relayItem: ({ hasUserId, itemId, userId }) => Relay.QL`
            fragment on Item {
                ${ShippingDetailsLink.getFragment('item', { hasUserId, itemId, userId })}
            }
        `,
            viewer: ({ hasUserId, itemId, userId }) => Relay.QL`
            fragment on Viewer {
                quoteRequestForAllIsEnabled: isEnabled(key: "enableRequestQuoteForAllLoggedIn")
                pdpBundleEnabled: isEnabled(key: "pdpBundle")
                ${ShippingDetailsLink.getFragment('viewer', { hasUserId, itemId, userId })}
            }
        `,
            conversation: () => Relay.QL`
            fragment on Conversation {
                transaction {
                    shipmentQuotes {
                        isCustom
                        status
                        totalAmount {
                            convertedAmountList {
                                amount
                                currency
                            }
                        }
                    }
                }
            }
        `,
        },
    }
);



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/Shipping/Blurb.jsx